import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, RotateCcw, Archive } from "lucide-react";
import clsx from "clsx";
import type { Board } from "../../types";
import { useStore } from "../../stores/store";
import { api, ApiError } from "../../lib/api";
import { getBoardColor, getBoardInitials } from "../../lib/boardVisuals";
import { BOARD_ICON_MAP } from "./boardIcons";

interface Props {
  open: boolean;
  onClose: () => void;
}

export function ArchivedBoardsModal({ open, onClose }: Props) {
  return (
    <AnimatePresence>{open && <ArchivedBody onClose={onClose} />}</AnimatePresence>
  );
}

function ArchivedBody({ onClose }: { onClose: () => void }) {
  const unarchiveBoard = useStore((s) => s.unarchiveBoard);
  const [list, setList] = useState<Board[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [restoring, setRestoring] = useState<number | null>(null);

  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        const all = await api.getBoards(true);
        if (!alive) return;
        const archived = all
          .filter((b) => b.archived)
          .sort((a, b) => {
            const da = a.archivedAt ? new Date(a.archivedAt).getTime() : 0;
            const db = b.archivedAt ? new Date(b.archivedAt).getTime() : 0;
            return db - da;
          });
        setList(archived);
      } catch (e) {
        if (e instanceof ApiError) setErr(e.message);
        else setErr("Não consegui carregar os arquivados.");
        setList([]);
      }
    }
    load();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function restore(id: number) {
    setRestoring(id);
    try {
      await unarchiveBoard(id);
      setList((prev) => (prev ? prev.filter((b) => b.id !== id) : prev));
    } catch (e) {
      if (e instanceof ApiError) setErr(e.message);
      else setErr("Falha ao restaurar.");
    } finally {
      setRestoring(null);
    }
  }

  function formatDate(iso: string | null): string {
    if (!iso) return "sem data";
    try {
      return new Date(iso).toLocaleDateString("pt-BR");
    } catch {
      return "sem data";
    }
  }

  return (
    <motion.div
      key="archived-overlay"
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.1 }}
    >
      <div className="absolute inset-0 bg-black/65 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={{ scale: 0.95, y: 10, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        exit={{ scale: 0.97, y: 6, opacity: 0 }}
        transition={{ type: "spring", damping: 24, stiffness: 300 }}
        className="glass relative w-[520px] max-w-[92vw] overflow-hidden rounded-2xl p-5 shadow-elevated"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-violet-400/40 to-transparent" />

        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-500/20 text-slate-300 ring-1 ring-slate-400/30">
              <Archive className="h-4 w-4" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-slate-100">Quadros arquivados</h3>
              <p className="mt-0.5 text-xs text-slate-400">
                Restaure para voltar a usar.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1 text-slate-400 hover:bg-white/5 hover:text-slate-100"
            aria-label="Fechar"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {err && <p className="mb-3 text-xs text-rose-300">{err}</p>}

        <div className="max-h-[60vh] space-y-2 overflow-y-auto pr-1">
          {list === null && (
            <p className="py-6 text-center text-sm text-slate-400">Carregando...</p>
          )}
          {list && list.length === 0 && (
            <p className="py-6 text-center text-sm text-slate-400">
              Nenhum quadro arquivado por enquanto.
            </p>
          )}
          {list?.map((b) => {
            const palette = getBoardColor(b.title, b.color);
            const Icon = b.icon ? BOARD_ICON_MAP[b.icon as keyof typeof BOARD_ICON_MAP] : null;
            return (
              <div
                key={b.id}
                className="flex items-center gap-3 rounded-xl bg-white/5 p-2 pr-3"
              >
                <div
                  className={clsx(
                    "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-xs font-semibold",
                    palette.bg,
                    palette.text,
                  )}
                >
                  {Icon ? <Icon className="h-4 w-4" /> : getBoardInitials(b.title)}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm text-slate-100">{b.title}</p>
                  <p className="text-xs text-slate-400">
                    arquivado em {formatDate(b.archivedAt)}
                  </p>
                </div>
                <button
                  onClick={() => restore(b.id)}
                  disabled={restoring === b.id}
                  className="flex items-center gap-1 rounded-lg bg-violet-500/90 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-violet-400 disabled:opacity-60"
                >
                  <RotateCcw className="h-3 w-3" />
                  {restoring === b.id ? "Restaurando..." : "Restaurar"}
                </button>
              </div>
            );
          })}
        </div>
      </motion.div>
    </motion.div>
  );
}
